import { useState, useRef, useCallback, useEffect } from 'react';

export interface DeviceOption {
  deviceId: string;
  label: string;
}

interface WebcamHook {
  /** Callback ref — attach to `<video ref={videoRef} />` so the stream binds when the element mounts after getUserMedia. */
  videoRef: React.RefCallback<HTMLVideoElement>;
  stream: MediaStream | null;
  isCameraOn: boolean;
  isMicOn: boolean;
  isPermissionDenied: boolean;
  audioInputDevices: DeviceOption[];
  videoInputDevices: DeviceOption[];
  selectedAudioInputId: string | null;
  selectedVideoInputId: string | null;
  micLevel: number;
  deviceError: string | null;
  toggleCamera: () => void;
  toggleMic: () => void;
  init: () => Promise<void>;
  refreshDevices: () => Promise<void>;
  applyDeviceSelection: (audioInputId: string | null, videoInputId: string | null) => Promise<void>;
  cleanup: () => void;
}

function attachStream(el: HTMLVideoElement, mediaStream: MediaStream) {
  el.srcObject = mediaStream;
  try {
    void Promise.resolve(el.play()).catch(() => {});
  } catch {
    // play() may throw synchronously in jsdom ("Not implemented") or if autoplay is blocked.
  }
}

interface UseWebcamOptions {
  /**
   * When `false`, `getUserMedia` will not request an audio track.  This avoids
   * a conflict on Android where an active `getUserMedia` audio stream prevents
   * the Web Speech API (`SpeechRecognition`) from capturing microphone input.
   * The mic-level meter will show 0 when audio capture is skipped.
   * @default true
   */
  requestAudio?: boolean;
}

export function useWebcam(options: UseWebcamOptions = {}): WebcamHook {
  const { requestAudio = true } = options;
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isPermissionDenied, setIsPermissionDenied] = useState(false);
  const [audioInputDevices, setAudioInputDevices] = useState<DeviceOption[]>([]);
  const [videoInputDevices, setVideoInputDevices] = useState<DeviceOption[]>([]);
  const [selectedAudioInputId, setSelectedAudioInputId] = useState<string | null>(null);
  const [selectedVideoInputId, setSelectedVideoInputId] = useState<string | null>(null);
  const [micLevel, setMicLevel] = useState(0);
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const selectedAudioInputIdRef = useRef<string | null>(null);
  const selectedVideoInputIdRef = useRef<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const stopMicLevelMonitor = useCallback(() => {
    if (animationFrameRef.current != null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setMicLevel(0);
  }, []);

  const startMicLevelMonitor = useCallback(
    (mediaStream: MediaStream) => {
      stopMicLevelMonitor();
      if (!mediaStream.getAudioTracks().length) {
        return;
      }

      const Ctx = window.AudioContext;
      if (!Ctx) {
        return;
      }

      try {
        const context = new Ctx();
        // Mobile browsers create AudioContext in 'suspended' state; resume so
        // the analyser receives data for the mic-level meter.
        if (context.state === 'suspended') {
          void context.resume();
        }
        const source = context.createMediaStreamSource(mediaStream);
        const analyser = context.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        audioContextRef.current = context;
        analyserRef.current = analyser;

        const sample = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteTimeDomainData(data);
          let sumSquares = 0;
          for (const v of data) {
            const normalized = (v - 128) / 128;
            sumSquares += normalized * normalized;
          }
          const rms = Math.sqrt(sumSquares / data.length);
          setMicLevel(Math.min(1, rms * 3));
          animationFrameRef.current = requestAnimationFrame(sample);
        };

        animationFrameRef.current = requestAnimationFrame(sample);
      } catch {
        setMicLevel(0);
      }
    },
    [stopMicLevelMonitor],
  );

  const videoRef = useCallback((node: HTMLVideoElement | null) => {
    videoElRef.current = node;
    if (node && streamRef.current) {
      attachStream(node, streamRef.current);
    }
  }, []);

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const audio = devices
      .filter((device) => device.kind === 'audioinput')
      .map((device, idx) => ({
        deviceId: device.deviceId,
        label: device.label || `Microphone ${idx + 1}`,
      }));
    const video = devices
      .filter((device) => device.kind === 'videoinput')
      .map((device, idx) => ({
        deviceId: device.deviceId,
        label: device.label || `Camera ${idx + 1}`,
      }));

    setAudioInputDevices(audio);
    setVideoInputDevices(video);

    if (!selectedAudioInputIdRef.current && audio.length > 0) {
      selectedAudioInputIdRef.current = audio[0].deviceId;
      setSelectedAudioInputId(audio[0].deviceId);
    }
    if (!selectedVideoInputIdRef.current && video.length > 0) {
      selectedVideoInputIdRef.current = video[0].deviceId;
      setSelectedVideoInputId(video[0].deviceId);
    }
  }, []);

  const applyTrackEnabledState = useCallback(
    (mediaStream: MediaStream) => {
      mediaStream.getAudioTracks().forEach((track) => {
        track.enabled = isMicOn;
      });
      mediaStream.getVideoTracks().forEach((track) => {
        track.enabled = isCameraOn;
      });
    },
    [isMicOn, isCameraOn],
  );

  const swapStream = useCallback(
    (nextStream: MediaStream) => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      streamRef.current = nextStream;
      setStream(nextStream);
      applyTrackEnabledState(nextStream);
      startMicLevelMonitor(nextStream);
    },
    [applyTrackEnabledState, startMicLevelMonitor],
  );

  const requestAudioRef = useRef(requestAudio);
  requestAudioRef.current = requestAudio;

  const acquireStream = useCallback(
    async (audioInputId: string | null, videoInputId: string | null) => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setDeviceError('Media devices are not available in this browser.');
        return;
      }

      const wantAudio = requestAudioRef.current;
      const constraints: MediaStreamConstraints = {
        audio: wantAudio ? (audioInputId ? { deviceId: { exact: audioInputId } } : true) : false,
        video: videoInputId ? { deviceId: { exact: videoInputId } } : true,
      };

      const nextStream = await navigator.mediaDevices.getUserMedia(constraints);
      swapStream(nextStream);
      setIsPermissionDenied(false);
      setDeviceError(null);
      selectedAudioInputIdRef.current = audioInputId;
      selectedVideoInputIdRef.current = videoInputId;
      setSelectedAudioInputId(audioInputId);
      setSelectedVideoInputId(videoInputId);
      await refreshDevices();
    },
    [refreshDevices, swapStream],
  );

  const init = useCallback(async () => {
    try {
      await acquireStream(selectedAudioInputIdRef.current, selectedVideoInputIdRef.current);
    } catch {
      setIsPermissionDenied(true);
      setDeviceError('Camera or microphone access was denied.');
    }
  }, [acquireStream]);

  const applyDeviceSelection = useCallback(
    async (audioInputId: string | null, videoInputId: string | null) => {
      try {
        await acquireStream(audioInputId, videoInputId);
      } catch {
        setDeviceError(
          'Could not switch to the selected device. Check if it is connected and not used by another app.',
        );
      }
    },
    [acquireStream],
  );

  const cleanup = useCallback(() => {
    if (videoElRef.current) {
      videoElRef.current.srcObject = null;
    }
    stopMicLevelMonitor();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setStream(null);
    }
  }, [stopMicLevelMonitor]);

  useEffect(() => {
    const el = videoElRef.current;
    if (!stream) {
      if (el) {
        el.srcObject = null;
      }
      return;
    }
    if (!el) {
      return;
    }
    attachStream(el, stream);
  }, [stream]);

  useEffect(() => {
    void refreshDevices();
  }, [refreshDevices]);

  const toggleCamera = useCallback(() => {
    if (!streamRef.current) return;
    const videoTracks = streamRef.current.getVideoTracks();
    videoTracks.forEach((track) => {
      track.enabled = !track.enabled;
    });
    setIsCameraOn((prev) => !prev);
  }, []);

  const toggleMic = useCallback(() => {
    if (!streamRef.current) return;
    const audioTracks = streamRef.current.getAudioTracks();
    audioTracks.forEach((track) => {
      track.enabled = !track.enabled;
    });
    setIsMicOn((prev) => !prev);
  }, []);

  useEffect(() => {
    return () => {
      stopMicLevelMonitor();
      if (videoElRef.current) {
        videoElRef.current.srcObject = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stopMicLevelMonitor]);

  return {
    videoRef,
    stream,
    isCameraOn,
    isMicOn,
    isPermissionDenied,
    audioInputDevices,
    videoInputDevices,
    selectedAudioInputId,
    selectedVideoInputId,
    micLevel,
    deviceError,
    toggleCamera,
    toggleMic,
    init,
    refreshDevices,
    applyDeviceSelection,
    cleanup,
  };
}
